interface WsAlertOptions {
  initNum: number
  timer?: number
  currentTime?: number // 当前时间戳（毫秒）
  callback: (data?: { realNum: number }) => void
}

// 全局配置缓存
let TIME_RANGES_CACHE: Array<{ start: number; end: number; timer: number }> | null = null
let TIME_RANGES_LOADING: Promise<Array<{ start: number; end: number; timer: number }>> | null = null

/**
 * 获取时间范围配置
 * @returns Promise<Array<{ start: number; end: number; timer: number }>>
 */
async function getTimeRanges(): Promise<Array<{ start: number; end: number; timer: number }>> {
  // 如果已经有缓存，直接返回
  if (TIME_RANGES_CACHE) {
    return TIME_RANGES_CACHE
  }

  // 如果正在加载中，等待加载完成
  if (TIME_RANGES_LOADING) {
    return TIME_RANGES_LOADING
  }

  // 开始加载配置
  TIME_RANGES_LOADING = fetch(
    `https://cdn-fx.nio.com/fx/pe-h5pages-fe/__fx_config__/ws_alert_config.prod.json?t=${Date.now()}`
  )
    .then((r) => r.json())
    .then((data) => {
      TIME_RANGES_CACHE = data
      TIME_RANGES_LOADING = null
      return data
    })
    .catch((error) => {
      // 如果请求失败，使用默认配置
      console.warn('Failed to load TIME_RANGES config, using default:', error)
      TIME_RANGES_CACHE = [{ start: 0, end: 24, timer: 0 }]
      TIME_RANGES_LOADING = null
      return TIME_RANGES_CACHE
    })

  return TIME_RANGES_LOADING
}

/**
 * 根据时间戳获取对应的时间范围timer值
 * @param timestamp 时间戳（毫秒）
 * @param timeRanges 时间范围配置
 * @returns timer值（毫秒）
 */
function getTimerByTime(
  timestamp: number,
  timeRanges: Array<{ start: number; end: number; timer: number }>
): number {
  const date = new Date(timestamp)
  const hour = date.getHours()

  for (const range of timeRanges) {
    if (hour >= range.start && hour < range.end) {
      return range.timer
    }
  }

  // 默认返回10000毫秒
  return 10000
}

export class WsAlert {
  private initNum: number
  private realNum: number
  private timer = 10000 // 默认值
  private callback: (data?: { realNum: number }) => void
  private countdownTimer: NodeJS.Timeout | null = null
  private isActive = true
  private isInitialized = false

  // 记录最后一次收到 WebSocket 更新的时间戳（毫秒）
  private lastUpdateTime: number

  constructor(options: WsAlertOptions) {
    this.initNum = options.initNum
    this.realNum = options.initNum
    this.callback = options.callback
    // 初始化时记录当前时间作为最后一次更新时间
    this.lastUpdateTime = options.currentTime || Date.now()

    // 异步初始化timer
    this.initializeTimer(options)
  }

  /**
   * 异步初始化timer
   */
  private async initializeTimer(options: WsAlertOptions): Promise<void> {
    try {
      // 如果传入了currentTime，根据时间范围设置timer
      if (options.currentTime) {
        const timeRanges = await getTimeRanges()
        this.timer = getTimerByTime(options.currentTime, timeRanges)
        console.log('获取配置的时间间隔-this.timer', this.timer)
        if (this.timer === 0) return
      } else {
        this.timer = options.timer || 10000
        console.log('组件传入的时间间隔-this.timer', this.timer)
      }

      this.isInitialized = true
      // 启动倒计时
      this.startCountdown()
    } catch (error) {
      console.error('Failed to initialize timer:', error)
      // 使用默认值
      this.timer = options.timer || 10000
      this.isInitialized = true
      this.startCountdown()
    }
  }

  /**
   * 更新 realNum 的值
   * @param newNum 新的数值
   */
  public updateRealNum(newNum: number): void {
    if (!this.isActive) return

    this.realNum = newNum
    // 每次收到 WebSocket 更新时，记录当前时间戳
    this.lastUpdateTime = Date.now()
  }

  /**
   * 手动触发比较函数
   */
  public compareFn(): void {
    if (!this.isActive) return

    // 停止倒计时
    this.stopCountdown()

    // 检查距离最后一次 WebSocket 更新是否超过阈值时间
    const now = Date.now()
    const timeSinceLastUpdate = now - this.lastUpdateTime

    // 如果超过阈值时间没有收到 WebSocket 更新，才触发 callback 上报错误
    if (
      this.initNum &&
      this.initNum === this.realNum &&
      this.timer > 0 &&
      timeSinceLastUpdate >= this.timer
    ) {
      this.callback({
        realNum: this.realNum,
      })
    }
  }

  /**
   * 启动倒计时
   */
  private startCountdown(): void {
    if (!this.isInitialized) {
      console.warn('WsAlert not initialized yet, countdown will start after initialization')
      return
    }

    this.countdownTimer = setTimeout(() => {
      this.compareFn()
    }, this.timer)
  }

  /**
   * 停止倒计时
   */
  private stopCountdown(): void {
    if (this.countdownTimer) {
      clearTimeout(this.countdownTimer)
      this.countdownTimer = null
    }
    this.isActive = false
  }

  /**
   * 获取当前 realNum 的值
   */
  public getRealNum(): number {
    return this.realNum
  }

  /**
   * 获取初始化的 initNum 的值
   */
  public getInitNum(): number {
    return this.initNum
  }

  /**
   * 检查实例是否处于活动状态
   */
  public isInstanceActive(): boolean {
    return this.isActive
  }

  /**
   * 销毁实例，清理资源
   */
  public destroy(): void {
    this.stopCountdown()
  }
}

// 导出默认实例创建函数
export default function createWsAlert(options: WsAlertOptions): WsAlert {
  return new WsAlert(options)
}
